import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../Common/PageHeader/PageHeader';
import { getMarketingPagesFull } from '../../services/adminApi';
import { updateDocumentTitle } from '../../utils/documentTitle';
import './AdminHubPage.css';
import './AdminMarketingPagesPage.css';

/**
 * AdminMarketingPagesPage: /admin/marketing-pages (Nitish, Oct 19 2026): "how are our marketing pages performing?"
 * Reads the hourly cache only (marketing_pages_report_service.collect_marketing_pages_full on recompute_live_metrics;
 * 503 when cold or stale, same as payments). Every definition lives server-side in that module's docstring; this page
 * only draws, and keeps the honesty rules visible: GA4 rates still settling (T+2) are greyed, paid % reads "too early"
 * for cohorts under 7 days and "filling" up to 30, and a rate over 0 visitors is a dash.
 * Same idiom as AdminScoreboardPage / AdminPaymentsPage: small SVG, hover or tap for the numbers, legend swatches are
 * SVG rects filled like the marks, axis labels in HTML so they never stretch, a closed Numbers table under each chart.
 */
const etTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'; } catch (e) { return ''; }
};
const ageText = (d) => {
  if (!d) return '';
  let secs = null;
  if (d.cache && typeof d.cache.age_seconds === 'number') secs = d.cache.age_seconds;
  else if (d.computed_at) secs = Math.max(0, (Date.now() - new Date(d.computed_at).getTime()) / 1000);
  if (secs === null || Number.isNaN(secs)) return 'fresh';
  const mins = Math.round(secs / 60);
  return mins < 1 ? 'computed just now' : `computed ${mins} min ago (${etTime(d.computed_at || (d.cache && d.cache.computed_at))})`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const dateLabel = (iso) => {
  const [, m, d] = String(iso || '').split('-').map(Number);
  return m && d ? `${MONTHS[m - 1]} ${d}` : String(iso || '');
};
export const fmtPct = (v) => (typeof v === 'number' ? `${v < 10 ? v.toFixed(1) : Math.round(v)}%` : '—');
const fmtN = (v) => (typeof v === 'number' ? v.toLocaleString() : '—');

/* Series = the six page groups, in the server's order, each its own validated slot (light + dark in the CSS). */
const SHORT = { home: 'Home', ai_employees: 'AI Emp.', super_agents: 'Super', agent_to_agent: 'A2A', blog: 'Blog', other: 'Other' };
const FALLBACK_GROUPS = [
  { key: 'home', label: 'Home' }, { key: 'ai_employees', label: 'AI Employees' }, { key: 'super_agents', label: 'Super Agent' },
  { key: 'agent_to_agent', label: 'Agent to Agent' }, { key: 'blog', label: 'Blog' }, { key: 'other', label: 'Everything else' },
];
export const seriesOf = (groups) => (groups && groups.length ? groups : FALLBACK_GROUPS)
  .map((g, i) => ({ k: g.key, label: g.label, short: SHORT[g.key] || g.label, color: `var(--mp-${i + 1})` }));

/** Paid % as the page shows it: a number only once the cohort is old enough to read. */
export const paidText = (pct, status) => {
  if (status === 'too_early') return { text: 'too early', grey: true, title: 'Cohort under 7 days old' };
  if (typeof pct !== 'number') return { text: '—', grey: false }; // no signups: nothing to fill
  if (status === 'still_filling') return { text: `${fmtPct(pct)} · filling`, grey: true, title: 'Cohort under 30 days old: the 30-day window is still open' };
  return { text: fmtPct(pct), grey: false };
};
/** A GA4-based rate: greyed while the week is still settling (T+2). */
export const rateText = (pct, settled) => (settled
  ? { text: fmtPct(pct), grey: false }
  : { text: typeof pct === 'number' ? `${fmtPct(pct)} · settling` : '—', grey: true, title: 'GA4 settles two days after the fact' });

/** The cached payload -> lead table + chart rows. Exported for the test. */
export const buildView = (data) => {
  if (!data || !Array.isArray(data.weeks) || !data.weekly) return null;
  const series = seriesOf(data.groups);
  const weekBy = Object.fromEntries(data.weeks.map((w) => [w.key, w]));
  const cell = (wk, g) => ((data.weekly[wk] || {})[g]) || {};
  const h = data.headline || {};
  const lead = series.map((s) => ({ ...s, cur: cell(h.week, s.k), prev: cell(h.prev_week, s.k) }));
  const weekly = data.weeks.map((w) => {
    const r = { id: w.key, label: dateLabel(w.start), tip: `Week of ${dateLabel(w.start)}${w.partial ? ' (so far)' : ''}`, settled: !!w.settled, raw: {} };
    series.forEach((s) => { const c = cell(w.key, s.k); r[s.k] = c.signup_pct_ex_agents; r.raw[s.k] = c; });
    return r;
  });
  const daily = (data.daily || []).map((d) => {
    const r = { id: d.date, label: dateLabel(d.date), tip: d.date, settled: !!d.settled };
    series.forEach((s) => { r[s.k] = (d.visitors || {})[s.k] || 0; });
    return r;
  });
  return { series, lead, weekly, daily, cur: weekBy[h.week] || null, prev: weekBy[h.prev_week] || null, noLanding: (data.no_landing || {})[h.week] || null };
};

/* Series swatch: an SVG rect filled exactly like the marks (a `background: var()` swatch fell back to the
   .adm-legend i teal; scoreboard fix, Oct 19 2026). */
const Swatch = ({ color, size = 10, label }) => (
  <svg className="mp-sw" width={size} height={size} viewBox="0 0 10 10" aria-hidden="true" style={{ fill: color }}
    data-testid={label ? 'mp-swatch' : undefined} data-label={label}>
    <rect width="10" height="10" rx="2" />
  </svg>
);

const Legend = ({ series }) => (
  <div className="adm-legend">
    {series.map((s) => <span key={s.k}><Swatch color={s.color} label={s.label} />{s.label}</span>)}
  </div>
);

const W = 560; const H = 150; const PAD = { l: 30, r: 8, t: 8, b: 4 };
const IW = W - PAD.l - PAD.r; const IH = H - PAD.t - PAD.b;

/* Multi-series lines. Settled stretches solid, the unsettled tail dashed under a "settling" band; a null value is a
   gap, never a zero. Hover or tap a column for its readout. */
const isNum = (v) => typeof v === 'number';

/* The y-axis top. clip: a rate over a handful of visitors can run far above the rest (first-touch signups over
   per-visit landings can even pass 100%), so when the largest value is over 1.5x the 95th percentile the axis stops
   at 1.2x that percentile and the outliers are pinned to the top (their exact values stay in the readout). */
export const axisTop = (vals, clip) => {
  const top = Math.max(1, ...vals);
  if (!clip || vals.length < 4) return { max: top, clipped: false };
  const sorted = [...vals].sort((a, b) => a - b);
  const p95 = sorted[Math.floor(0.95 * (sorted.length - 1))];
  return p95 > 0 && top > 1.5 * p95 ? { max: p95 * 1.2, clipped: true } : { max: top, clipped: false };
};

const TrendLines = ({ rows, series, label, fmt, tipValue, labelEvery = 1, clip = false }) => {
  const [hi, setHi] = useState(null);
  if (!rows.length) return null;
  const vals = rows.flatMap((r) => series.map((s) => r[s.k])).filter(isNum);
  const { max, clipped } = axisTop(vals, clip);
  const n = rows.length;
  const x = (i) => PAD.l + (n === 1 ? IW / 2 : (i / (n - 1)) * IW);
  const y = (v) => PAD.t + IH - (Math.min(v, max) / max) * IH;
  // A value with no numeric neighbour has no segment to sit on: draw it as a dot (a zero-length round-capped line,
  // so the non-uniform viewBox stretch cannot turn it into an ellipse).
  const lone = (k) => rows.map((r, i) => (isNum(r[k]) && !isNum((rows[i - 1] || {})[k]) && !isNum((rows[i + 1] || {})[k]) ? i : -1)).filter((i) => i >= 0);
  const seg = (k, dashed) => rows.map((r, i) => {
    if (!i) return '';
    const a = rows[i - 1][k]; const b = r[k];
    if (typeof a !== 'number' || typeof b !== 'number') return '';
    if ((rows[i - 1].settled && r.settled) === dashed) return '';
    return `M${x(i - 1).toFixed(1)},${y(a).toFixed(1)} L${x(i).toFixed(1)},${y(b).toFixed(1)}`;
  }).join(' ');
  const firstOpen = rows.findIndex((r) => !r.settled);
  const step = n > 1 ? IW / (n - 1) : IW;
  const pct = (px) => (px / W) * 100;
  const hr = hi !== null ? rows[hi] : null;
  // Axis labels every labelEvery-th point, counted back from the newest so the latest point is always labelled.
  const shown = rows.map((_r, i) => i).filter((i) => (n - 1 - i) % labelEvery === 0);
  return (
    <div onMouseLeave={() => setHi(null)}>
      <div className="adm-chart-wrap">
        <span className="mp-ymax">{fmt(max)}{clipped ? <i className="mp-clip-note" data-testid="mp-clipped"> · higher weeks pinned to the top</i> : null}</span>
        {firstOpen >= 0 ? <span className="mp-band-l">settling</span> : null}
        <svg className="adm-chart mp-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
          {firstOpen >= 0 ? <rect className="mp-band" x={firstOpen ? x(firstOpen) - step / 2 : PAD.l} width={W - PAD.r - (firstOpen ? x(firstOpen) - step / 2 : PAD.l)} y={PAD.t} height={IH} /> : null}
          <line className="axis" x1={PAD.l} x2={W - PAD.r} y1={PAD.t + IH} y2={PAD.t + IH} />
          {series.map((s) => (
            <g key={s.k} style={{ stroke: s.color }} className="mp-series">
              <path d={seg(s.k, false)} className="mp-line" vectorEffect="non-scaling-stroke" />
              <path d={seg(s.k, true)} className="mp-line mp-dash" vectorEffect="non-scaling-stroke" />
              {lone(s.k).map((i) => (
                <line key={i} className={`mp-dot${rows[i].settled ? '' : ' mp-dot-open'}`} data-testid="mp-dot" x1={x(i)} x2={x(i)} y1={y(rows[i][s.k])} y2={y(rows[i][s.k])} vectorEffect="non-scaling-stroke" />
              ))}
            </g>
          ))}
          {hr ? <line className="axis" x1={x(hi)} x2={x(hi)} y1={PAD.t} y2={PAD.t + IH} /> : null}
          {rows.map((r, i) => (
            <rect key={r.id} className="hit" data-testid="mp-hit" x={x(i) - step / 2} width={step} y={PAD.t} height={IH}
              onMouseEnter={() => setHi(i)} onClick={() => setHi(i)} />
          ))}
        </svg>
        {hr ? (
          <div className="adm-tip mp-tip" style={{ left: `clamp(110px, ${pct(x(hi))}%, calc(100% - 110px))` }} role="status">
            <div className="adm-tip-h">{hr.tip}{hr.settled ? '' : ' · settling'}</div>
            {series.map((s) => (
              <div className="adm-tip-r" key={s.k}><Swatch color={s.color} size={8} />{s.label}<b>{tipValue(hr, s.k)}</b></div>
            ))}
          </div>
        ) : null}
      </div>
      <div className="mp-strip" aria-hidden="true">
        {shown.map((i, j) => (
          <span key={rows[i].id} className={(shown.length - 1 - j) % 2 ? 'mp-odd' : ''} style={{ left: `${pct(x(i))}%` }}>{rows[i].label}</span>
        ))}
      </div>
    </div>
  );
};

const Cell = ({ t }) => <span className={t.grey ? 'mp-grey' : ''} title={t.title}>{t.text}</span>;

/* The exact numbers behind a chart, closed by default; short headers, the full name in the header title. */
const Numbers = ({ rows, series, value, first }) => (
  <details className="mp-numbers" data-testid="mp-numbers">
    <summary>Numbers</summary>
    <div className="mp-table-wrap">
      <table className="mp-table">
        <thead><tr><th>{first}</th>{series.map((s) => <th key={s.k} title={s.label}>{s.short}</th>)}</tr></thead>
        <tbody>
          {rows.slice().reverse().map((r) => (
            <tr key={r.id}><td>{r.label}</td>{series.map((s) => <td key={s.k}><Cell t={value(r, s.k)} /></td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
);

const LEAD_COLS = [
  { k: 'visitors', label: 'Visitors', title: 'GA4 users who landed on the group, that week' },
  { k: 'signups', label: 'Signups', title: 'Real signups whose first page was in the group' },
  { k: 'signup_pct', label: 'Signup %', title: 'Signups / visitors' },
  { k: 'signup_pct_ex_agents', label: 'Ex-agents', title: 'Signup % without Claude / MCP and API-key users (the headline)', hl: true },
  { k: 'paid', label: 'Paid', title: 'Paid within 30 days of signup' },
  { k: 'paid_pct', label: 'Paid %', title: 'Paid / signups' },
];

const leadText = (row, col, week) => {
  const v = row[col.k];
  if (col.k === 'paid_pct') return paidText(v, week && week.paid_status);
  if (col.k === 'signup_pct' || col.k === 'signup_pct_ex_agents') return rateText(v, !week || week.settled);
  return { text: fmtN(v), grey: false };
};

const LeadTable = ({ view }) => (
  <div className="mp-table-wrap mp-lead-wrap">
    <table className="mp-table mp-lead" data-testid="mp-lead">
      <thead>
        <tr><th>Page</th>{LEAD_COLS.map((c) => <th key={c.k} title={c.title} className={c.hl ? 'mp-hl' : ''}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {view.lead.map((r) => (
          <tr key={r.k}>
            <td><Swatch color={r.color} size={8} /> {r.label}</td>
            {LEAD_COLS.map((c) => (
              <td key={c.k} className={c.hl ? 'mp-hl' : ''}>
                <Cell t={leadText(r.cur, c, view.cur)} />
                <small className="mp-prev"><Cell t={leadText(r.prev, c, view.prev)} /></small>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AdminMarketingPagesPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const res = await getMarketingPagesFull();
      setData(res.data);
    } catch (e) {
      setError(String(e?.response?.data?.detail || e?.message || 'unavailable'));
    }
    setBusy(false);
  }, []);
  useEffect(() => { updateDocumentTitle('Marketing Pages · Admin'); load(); }, [load]);

  const view = useMemo(() => buildView(data), [data]);
  const ex = data && data.ga4 && data.ga4.excluded_users;
  const nl = view && view.noLanding;

  return (
    <div className="adm-page mp-page">
      <p className="adm-crumb"><Link to="/admin">Admin</Link> › Marketing Pages</p>
      <PageHeader
        title="Marketing Pages"
        subtitle="How each marketing page turns visitors into signups and payers. Mon-Sun weeks; internal, test, duplicate and throwaway accounts left out."
        actions={<Button icon={<ReloadOutlined />} onClick={load} loading={busy}>Refresh</Button>}
      />
      <p className="adm-stamp">
        {data ? <span>Hourly cache · {ageText(data)} · GA4 visitors, real signups, payments · hover or tap a chart for exact numbers</span> : <span>{error ? 'Not serving' : 'Loading…'}</span>}
        {data && error ? <span className="mp-stale" role="alert">Refresh failed, showing the earlier load: {error}</span> : null}
      </p>
      {error && !data ? <div className="adm-empty">The marketing pages cache is not serving: {error}</div> : null}
      {data && !view ? <div className="adm-empty">This cache has no marketing pages payload yet. It appears after the next hourly beat.</div> : null}
      {view ? (
        <div className="mp-stack">
          <div className="adm-panel">
            <h3 className="adm-panel-h">
              {view.cur ? `Week of ${dateLabel(view.cur.start)}` : 'No settled week yet'}
              {view.prev ? <span className="mp-vs"> · small figures: week of {dateLabel(view.prev.start)}</span> : null}
            </h3>
            <p className="adm-panel-s">The last complete week GA4 has settled. Ex-agents = signup % without Claude / MCP and API-key users, the headline. Paid = paid within 30 days of signup; it reads too early under 7 days and filling up to 30.</p>
            <LeadTable view={view} />
            {nl ? (
              <p className="mp-note" data-testid="mp-no-landing">
                No landing recorded: {nl.signups} signup{nl.signups === 1 ? '' : 's'} ({nl.signups_ex_agents} ex-agents, {nl.paid} paid) that week, in no row.
              </p>
            ) : null}
          </div>
          <div className="adm-panel">
            <h3 className="adm-panel-h">Signup % ex-agents, weekly</h3>
            <p className="adm-panel-s">Signups without agent users / visitors, per page group, last 12 weeks. Dashed under the band = GA4 still settling. A gap = no visitors that week.</p>
            <TrendLines rows={view.weekly} series={view.series} label="Weekly signup percent without agent users, per page group" fmt={fmtPct} clip
              tipValue={(r, k) => { const c = r.raw[k] || {}; return `${fmtPct(r[k])} (${fmtN(c.signups_ex_agents)} / ${fmtN(c.visitors)})`; }} />
            <Legend series={view.series} />
            <Numbers rows={view.weekly} series={view.series} first="Week" value={(r, k) => rateText(r[k], r.settled)} />
          </div>
          <div className="adm-panel">
            <h3 className="adm-panel-h">Visitors per day</h3>
            <p className="adm-panel-s">GA4 users by landing page group, last 30 days through yesterday. The last two days are still settling and may rise.</p>
            <TrendLines rows={view.daily} series={view.series} label="Daily visitors per page group" fmt={fmtN} labelEvery={5}
              tipValue={(r, k) => fmtN(r[k])} />
            <Legend series={view.series} />
            <Numbers rows={view.daily} series={view.series} first="Day" value={(r, k) => ({ text: fmtN(r[k]), grey: !r.settled })} />
          </div>
          <p className="mp-note">
            Visitors are GA4 users by the page a visit landed on, distinct per week, summed across a group's pages (a person landing on two pages of one group counts twice). GA4 runs on Pacific days, signups on ET days. Signups go to the first page the person ever landed on.
            {ex ? ` Left out of visitors over the 12 weeks: ${fmtN(ex.empty)} with no landing page (app sessions), ${fmtN(ex.not_a_path)} with none recorded by GA4${ex.ga4_other ? `, ${fmtN(ex.ga4_other)} on pages GA4 lumped into "(other)"` : ''}.` : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default AdminMarketingPagesPage;
